import { useCallback, useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/storybook/components/Toast/Toast';
import useChatbotStore from '../store/useChatbotStore';
import { chatbotMutationKeys } from './chatbotMutationKeys';
import { getStreamCallbacks, stopChatbotRequest, waitForChatbotRequest } from './chatbotRequestLifecycle';
import { buildSendChatbotVariables, NEW_CONVERSATION_ID, sanitizeActiveFilters } from './chatbotRequest';
import type { SendChatbotVariables } from './chatbotRequest';
import useChatbotSession, { isSessionError } from './useChatbotSession';
import useChatbotStream, { isStreamError } from './useChatbotStream';
import useChatbotStop from './useChatbotStop';
import useActiveFiltersForChatbot from './useActiveFiltersForChatbot';
import useConversationProcessingLock from './useConversationProcessingLock';
import { handleChatbotFailure } from './handleChatbotFailure';
import type { ChatbotSessionRequest, ChatbotSessionResponse } from '@/models/chatbot';

interface UseChatbotReturn {
  isConversationBusy: boolean;
  sendMessage: (message: string) => Promise<void>;
  retryLastMessage: () => Promise<void>;
  stopMessage: () => Promise<void>;
  handleNewChat: () => void;
  handleCloseDrawer: () => void;
}

type ReleaseLock = () => void;
type AcquireLock = (targetConversationId?: string | null) => Promise<ReleaseLock | null>;
type MutateChatbot = (variables: SendChatbotVariables) => Promise<void>;

interface ProcessingRef {
  current: boolean;
}

interface CancellableSendChatbotVariables extends SendChatbotVariables {
  controller: AbortController;
}

const getStoreActions = () => useChatbotStore.getState();
function createRetryLastMessage(
  mutateAsync: MutateChatbot,
  t: (key: string) => string,
  acquireLock: AcquireLock,
  isProcessingRef: ProcessingRef,
): () => Promise<void> {
  return async (): Promise<void> => {
    const { lastRequest, activeConversationId: currentConversationId } = useChatbotStore.getState();
    if (!lastRequest || isProcessingRef.current) return;

    isProcessingRef.current = true;
    const releaseLock = await acquireLock(currentConversationId);
    if (!releaseLock) {
      isProcessingRef.current = false;
      return;
    }

    const { request, promptFilters } = buildSendChatbotVariables(
      lastRequest.message,
      currentConversationId ?? lastRequest.conversation_id ?? NEW_CONVERSATION_ID,
      lastRequest.active_filters,
      t,
    );

    try {
      await mutateAsync({ request, promptFilters, isNewUserMessage: false });
    } catch {
      // Failure handling lives in the mutation `onError` callback.
    } finally {
      releaseLock();
      isProcessingRef.current = false;
    }
  };
}

const useChatbot = (): UseChatbotReturn => {
  const isStreaming = useChatbotStore(s => s.isStreaming);
  const selectedConversationId = useChatbotStore(s => s.activeConversationId);
  const { t } = useTranslation();
  const { makeToast } = useToast();
  const { createSession } = useChatbotSession();
  const { startStream, abortStream } = useChatbotStream();
  const { stopStream } = useChatbotStop();
  const activeFilters = useActiveFiltersForChatbot();
  const { isLockedByAnotherTab, acquireLock } = useConversationProcessingLock(selectedConversationId);
  const queryClient = useQueryClient();
  const isProcessingRef = useRef(false);
  const activeRequestRef = useRef<AbortController | null>(null);

  const executeStream = useCallback(
    async (request: ChatbotSessionRequest, isRetry: boolean, signal: AbortSignal): Promise<void> => {
      if (signal.aborted) throw signal.reason;
      if (request.conversation_id === NEW_CONVERSATION_ID) {
        getStoreActions().setPinnedFilters(sanitizeActiveFilters(request.active_filters) ?? null);
      }

      let session: ChatbotSessionResponse;

      try {
        session = await waitForChatbotRequest(createSession(request), signal);
      } catch (error: unknown) {
        if (
          isSessionError(error) &&
          error.status === 404 &&
          !isRetry &&
          request.conversation_id !== undefined &&
          request.conversation_id !== NEW_CONVERSATION_ID
        ) {
          getStoreActions().setActiveSession({ conversationId: null, sessionId: null });
          await executeStream({ ...request, conversation_id: NEW_CONVERSATION_ID }, true, signal);
          return;
        }

        throw error;
      }

      if (signal.aborted) throw signal.reason;
      getStoreActions().setActiveSession({
        conversationId: session.conversation_id ?? null,
        sessionId: session.session_id,
      });

      const releaseCreatedConversationLock =
        request.conversation_id === NEW_CONVERSATION_ID && session.conversation_id
          ? await acquireLock(session.conversation_id)
          : (): void => undefined;

      try {
        if (signal.aborted) throw signal.reason;
        await waitForChatbotRequest(
          startStream(session.stream_url, getStreamCallbacks(session.session_id, signal, queryClient)),
          signal,
        );
      } catch (error: unknown) {
        if (isStreamError(error) && error.status === 401 && !isRetry) {
          getStoreActions().setError(null);
          await executeStream(request, true, signal);
          return;
        }

        if (isStreamError(error) && error.status === 429) {
          getStoreActions().setError({ message: error.message, retryable: false });
          getStoreActions().finalizeAiMessage();
          return;
        }

        throw error;
      } finally {
        releaseCreatedConversationLock?.();
      }
    },
    [acquireLock, createSession, queryClient, startStream],
  );

  const handleStreamFailure = useCallback((error: unknown): void => handleChatbotFailure(error, t), [t]);

  const sendMutation = useMutation<void, unknown, CancellableSendChatbotVariables>({
    mutationKey: chatbotMutationKeys.sendMessage,
    onMutate: ({ request, promptFilters, isNewUserMessage }) => {
      const store = getStoreActions();
      if (isNewUserMessage) {
        store.addUserMessage(request.message);
        store.setLastRequest(request);
      }
      store.resetStreamingState();
      store.setStreamingPromptFilters(promptFilters);
      store.setStreaming(true);
      store.setError(null);
    },
    mutationFn: ({ request, controller }) => executeStream(request, false, controller.signal),
    onError: (error, { controller }) => {
      if (!controller.signal.aborted) handleStreamFailure(error);
    },
    onSettled: (_result, _error, { controller }) => {
      if (activeRequestRef.current === controller) {
        getStoreActions().setStreaming(false);
        activeRequestRef.current = null;
      }
    },
  });

  const { mutateAsync: mutateRequest } = sendMutation;
  const mutateAsync = useCallback(
    async (variables: SendChatbotVariables): Promise<void> => {
      const controller = new AbortController();
      activeRequestRef.current = controller;
      await mutateRequest({ ...variables, controller });
    },
    [mutateRequest],
  );

  const sendMessage = useCallback(
    async (message: string): Promise<void> => {
      if (isProcessingRef.current || isStreaming || useChatbotStore.getState().isStopping) return;

      const trimmed = message.trim();
      if (!trimmed) return;

      const { activeConversationId: currentConversationId } = useChatbotStore.getState();
      isProcessingRef.current = true;
      const releaseLock = await acquireLock(currentConversationId);
      if (!releaseLock) {
        isProcessingRef.current = false;
        return;
      }

      const { request, promptFilters } = buildSendChatbotVariables(trimmed, currentConversationId, activeFilters, t);
      try {
        await mutateAsync({ request, promptFilters, isNewUserMessage: true }).catch((): void => undefined);
      } finally {
        isProcessingRef.current = false;
        releaseLock();
      }
    },
    [acquireLock, activeFilters, isStreaming, mutateAsync, t],
  );

  const retryLastMessage = useMemo(
    () => createRetryLastMessage(mutateAsync, t, acquireLock, isProcessingRef),
    [acquireLock, mutateAsync, t],
  );

  const stopMessage = useCallback(
    () =>
      stopChatbotRequest({
        requestRef: activeRequestRef,
        abortStream,
        stopStream,
        onError: () => makeToast({ content: t('components.aiChatDrawer.stop.errorToast'), appearance: 'error' }),
      }),
    [abortStream, makeToast, stopStream, t],
  );

  const handleNewChat = useCallback(() => {
    activeRequestRef.current?.abort();
    abortStream();
    getStoreActions().clearConversation();
  }, [abortStream]);

  const handleCloseDrawer = useCallback(() => {
    getStoreActions().setDrawerOpen(false);
  }, []);

  return {
    isConversationBusy: isLockedByAnotherTab,
    sendMessage,
    retryLastMessage,
    stopMessage,
    handleNewChat,
    handleCloseDrawer,
  };
};

export default useChatbot;
